import { useGcsStore } from '../../../store/useGcsStore';
import {
  VerticalStepper,
  type VerticalStepperStep,
  ConnectorStatus
} from '../../../components/general/VerticalStepper';
import { StepStatus } from '../../../types/ui/stepStatus';
import { en } from '../../../locales/en';
import { MissionWaypointType, type MissionCreatedNotification } from '../../../types/gcs/mission';
import type { VehicleId } from '../../../types/gcs/vehicle';
import type { Launchpad } from '../../../types/gcs/launchpad';
import { selectVehicleStatusCategory } from '../../../store/selectors/selectVehicleStatusCategory';
import {
  getVehicleStatusBackgroundColor,
  getVehicleStatusForegroundColor
} from '../../../types/ui/vehicleStatusCategory';
import { VehicleStatusCategory } from '../../../types/ui/vehicleStatusCategory';
import { getServerNameCode } from '../../../config/gcs/gcsConfigs';

const DEFAULT_STEP_COLOR = getVehicleStatusForegroundColor(VehicleStatusCategory.ON_GROUND);
const DEFAULT_STEP_CONNECTOR_COLOR = getVehicleStatusBackgroundColor(VehicleStatusCategory.ON_GROUND);

interface MissionStepperProps {
  gcsId: string;
  vehicleId: VehicleId;
}

type StepBase = Omit<VerticalStepperStep, 'status' | 'connectorStatus'>;

function getWaypointStep(
  waypointType: MissionWaypointType,
  liveMission: MissionCreatedNotification,
  launchpad: Launchpad | undefined,
  nameCode: string
): StepBase {
  const launchpadText = launchpad ? `${en.mapMarkers.launchpad} ${launchpad.letter}` : en.mapMarkers.launchpad;
  switch (waypointType) {
    case MissionWaypointType.LAUNCHPAD:
      return { label: nameCode, text: launchpadText };
    case MissionWaypointType.PICKUP_POINT:
      return { label: en.missionStepper.pickupPoint, text: liveMission.pickupPoint?.name ?? '—' };
    case MissionWaypointType.DOCK_POINT:
      return { label: en.missionStepper.dockPoint, text: liveMission.dockPoint?.name ?? '—' };
    case MissionWaypointType.DELIVERY_POINT:
      return { label: en.missionStepper.deliveryPoint, text: liveMission.package?.order?.deliveryPoint?.name ?? '—' };
  }
}

// TODO: Step statuses and connector statuses will be computed from mission progress in a later stage.
function buildSteps(
  liveMission: MissionCreatedNotification | null,
  launchpad: Launchpad | undefined,
  nameCode: string
): VerticalStepperStep[] {
  const launchpadText = launchpad ? `${en.mapMarkers.launchpad} ${launchpad.letter}` : en.mapMarkers.launchpad;
  const launchpadStep: StepBase = { label: nameCode, text: launchpadText };

  if (!liveMission) {
    return [{ ...launchpadStep, status: StepStatus.NOT_DONE }];
  }

  const waypointTypes: MissionWaypointType[] = [MissionWaypointType.LAUNCHPAD];
  if (liveMission.dockPoint) waypointTypes.push(MissionWaypointType.DOCK_POINT);
  else if (liveMission.pickupPoint) waypointTypes.push(MissionWaypointType.PICKUP_POINT);
  if (liveMission.package) waypointTypes.push(MissionWaypointType.DELIVERY_POINT);
  waypointTypes.push(MissionWaypointType.LAUNCHPAD);

  return waypointTypes.map((waypointType, i) => {
    const isLast = i === waypointTypes.length - 1;
    return {
      ...getWaypointStep(waypointType, liveMission, launchpad, nameCode),
      status: StepStatus.NOT_DONE,
      connectorStatus: isLast ? undefined : ConnectorStatus.NOT_DONE
    };
  });
}

export function MissionStepper({ gcsId, vehicleId }: MissionStepperProps) {
  const category = useGcsStore((state) => selectVehicleStatusCategory(state.data, gcsId, vehicleId));

  const stepColor = category ? getVehicleStatusForegroundColor(category) : DEFAULT_STEP_COLOR;
  const stepConnectorColor = category ? getVehicleStatusBackgroundColor(category) : DEFAULT_STEP_CONNECTOR_COLOR;

  const liveMission = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData) return null;
    return gcsData.liveMissionsMap.get(vehicleId) ?? null;
  });

  const launchpad = useGcsStore((state) => {
    const gcsData = state.data.get(gcsId);
    if (!gcsData) return undefined;
    const launchpadId = gcsData.vehicleLaunchpadMap.get(vehicleId);
    if (!launchpadId) return undefined;
    return gcsData.launchpads.data?.find((l) => l.id === launchpadId);
  });

  const nameCode = getServerNameCode(gcsId);
  const steps = buildSteps(liveMission, launchpad, nameCode);

  return (
    <div
      style={{
        border: 'var(--card-border-stroke) var(--card-border-style) var(--card-border-color)',
        borderRadius: 'var(--card-border-radius)',
        padding: '12px 16px',
        backgroundColor: '#ffffff'
      }}
    >
      <VerticalStepper steps={steps} stepColor={stepColor} stepConnectorColor={stepConnectorColor} />
    </div>
  );
}
